import store from '@/store';
import Http from '@/services/http';
import MonitoringService from '@/domain/services/generalPanel/dashboard';
import {
  InstallationStatus,
  BasicInstallation,
  InstallationRequest,
  InstallationsPagedRequest,
  InstallationsPagedResponse,
  InstallationOptionsRequest,
  InstallationCompleteResponse,
  InstallationsBasicResponse,
} from 'sigma-mappings';
import editInstallationsFieldsDefinitions
  from '@/domain/data/installation/editInstallationsFieldsDefinitions';
import translate from '@/services/translate';

const urls = {
  getInstallation: '/instalacao',
  getInstallationList: '/instalacao/listagem',
  installationData: installationId => `/instalacao/${installationId}`,
  getInstallationByCompany: companyId => `/empresa/${companyId}/instalacoes`,
  getInstallationByCompaniesOrRegional: '/usuario/empresas/instalacoes',
};

/* istanbul ignore next */
const error = {
  errorHandling: {
    responseMessageParams: {
      $MA001: (data) => {
        const returnedField = editInstallationsFieldsDefinitions[data.field];
        if (!returnedField) return null;

        return {
          NomeDoCampo: translate(returnedField.label),
        };
      },
      $ME055: (data) => {
        const returnedField = editInstallationsFieldsDefinitions[data.field];
        if (!returnedField) return null;
        const param = data.parameters[0];
        return {
          0: param,
        };
      },
      $MA029: (data) => {
        const returnedField = editInstallationsFieldsDefinitions[data.field];
        if (!returnedField) return null;
        return {
          FaixaInicial: returnedField.min,
          FaixaFinal: returnedField.max,
          NomeDoCampo: translate(returnedField.label),
        };
      },
      $MA051: (data) => {
        const returnedField = editInstallationsFieldsDefinitions[data.field];
        if (!returnedField) return null;
        return {
          maxLength: returnedField.max,
          fieldName: translate(returnedField.label),
        };
      },
    },
  },
};

function reducePush(result, newGroup) {
  result.push(...newGroup);
  return result;
}

function reduceExcludeDuplicated(equalComparer) {
  return (result, newItem) => {
    if (!result.some(existing => equalComparer(existing, newItem))) {
      result.push(newItem);
    }
    return result;
  };
}

export default {
  /**
   * Recupera uma lista de instalações que o usuário tem acesso, possibilita o filtro por empresa
   * @param {*} companyId
   * @returns {Promise<*>} Lista de instalacoes com id e nome
   * GET /instalacao - Não tem como você filtrar as instalações por empresas.
   * GET /instalacao/lookup - Você só consegue filtrar por UM ID de empresa ou REGIONAL
   * POST /usuario/empresas/instalacoes -
   *    Você pode passar uma lista de empresas
   *    e/ou regionais e ele irá retornar a lista de instalações.
   */
  getInstallations(companyId) {
    const http = new Http();
    if (companyId) {
      return http.get(urls.getInstallationByCompany(companyId))
        .then(({ data }) => data.map(item => new InstallationsBasicResponse(item)));
    }
    return http.get(urls.getInstallation)
      .then(({ data }) => data.map(item => new InstallationsBasicResponse(item)));
  },
  getInstallationList(payload) {
    const http = new Http();
    const mappedPayload = new InstallationsPagedRequest(payload);
      return http.post(urls.getInstallationList, mappedPayload)
        .then(({ data }) => new InstallationsPagedResponse(data));
  },
  getInstallationsByCompaniesOrRegional(options) {
    const http = new Http();
    const apiModel = new InstallationOptionsRequest(options);
    return http
      .post(urls.getInstallationByCompaniesOrRegional, apiModel)
        .then(({ data }) => data.map(item => new InstallationCompleteResponse(item)));
  },
  getAccessInstallationData(installationId) {
    const http = new Http();
      return http.get(urls.installationData(installationId))
        .then(({ data }) => new BasicInstallation(data));
  },
  editAccessInstallationData(installationId, payload) {
    const http = new Http(error);
    const mappedPayload = new InstallationRequest(payload);
      return http.put(urls.installationData(installationId), mappedPayload)
        .then(({ data }) => data);
  },
  async loadAccessInstallationData(installationId) {
    await this.loadUserAccessibleInstallations();
    store.dispatch('setInstallation', await this.getAccessInstallationData(installationId));
  },
  clearAccessInstallationData() {
    this.clearUserAccessibleInstallations();
    store.dispatch('setInstallation');
  },
  async loadUserAccessibleInstallations() {
    if (store.getters.isUserAccessibleInstallationsLoaded) {
      return;
    }

    const companiesData = await MonitoringService.getInstallationMenuData();
    const regionalData = companiesData
      .map(company => company.regional)
      .reduce(reducePush, []);
    const installations = regionalData
      .map(regional => (regional.installations ? regional.installations.filter(
        installation => (installation.status !== InstallationStatus.BLOCKED
           || installation.status !== InstallationStatus.INACTIVE),
      ) : []))
      .reduce(reducePush, [])
      .reduce(reduceExcludeDuplicated((existing, newItem) => existing.id === newItem.id), [])
      .sort((a, b) => a.name.localeCompare(b.name));

    store.dispatch('setUserAccessibleInstallations', installations);
  },
  clearUserAccessibleInstallations() {
    store.dispatch('setUserAccessibleInstallations');
  },
};
